import { EventEmitter } from "events";

function createCallbackId() {
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export class ShareJsSocket extends EventEmitter {
    constructor(url, options = {}) {
        super();
        this.url = url;
        this.WebSocketImpl = options.WebSocketImpl || window.WebSocket;
        this.reconnectDelay = options.reconnectDelay || 1000;
        this._socket = null;
        this.connected = false;
        this.publicId = options.publicId || createCallbackId();
        this.callbacks = new Map();
        this.reconnectTimer = null;
        this.shouldReconnect = true;
        this.socket = {
            get connected() {
                return !!thisRef.connected;
            },
            get sessionid() {
                return thisRef.publicId;
            },
            get transport() {
                return thisRef._socket || {};
            },
            connect() {
                thisRef.connect();
            },
            disconnect() {
                thisRef.close();
            },
        };

        const thisRef = this;
    }

    trigger(...args) {
        return super.emit(...args);
    }

    failPendingCallbacks(error) {
        const callbacks = Array.from(this.callbacks.values());
        this.callbacks.clear();
        for (const callback of callbacks) {
            try {
                callback(error);
            } catch (callbackError) {
                console.error(callbackError);
            }
        }
    }

    connect() {
        if (this._socket) {
            return this._socket;
        }
        if (this.reconnectTimer) {
            window.clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }

        this.publicId = createCallbackId();

        const socket = new this.WebSocketImpl(this.url);
        this._socket = socket;

        socket.onopen = () => {
            this.connected = true;
            this.trigger("connect");
        };

        socket.onclose = () => {
            this.connected = false;
            if (this._socket === socket) {
                this._socket = null;
            }
            this.failPendingCallbacks({ message: "socket_closed" });
            this.trigger("disconnect");
            if (this.shouldReconnect) {
                this.reconnectTimer = window.setTimeout(() => {
                    this.reconnectTimer = null;
                    this.connect();
                }, this.reconnectDelay);
            }
        };

        socket.onerror = () => {
            this.trigger("error", new Error("sharejs websocket error"));
        };

        socket.onmessage = (messageEvent) => {
            let message;
            try {
                message = JSON.parse(messageEvent.data);
            } catch (error) {
                this.trigger("error", new Error("invalid_sharejs_socket_message"));
                return;
            }
            if (message.type === "callback") {
                const callback = this.callbacks.get(message.callback_id);
                if (callback) {
                    this.callbacks.delete(message.callback_id);
                    callback(...(message.args || []));
                }
                return;
            }

            if (message.type === "event") {
                this.trigger(message.event, ...(message.args || []));
                return;
            }
            this.trigger(message.type, message.payload, message.user_id);
        };

        return socket;
    }

    emit(eventName, ...args) {
        if (eventName !== "connect" && eventName !== "disconnect" && eventName !== "error") {
            const callback = typeof args[args.length - 1] === "function" ? args.pop() : null;
            const callbackId = callback ? createCallbackId() : null;

            if (callbackId) {
                this.callbacks.set(callbackId, callback);
            }

            if (!this._socket || this._socket.readyState !== this.WebSocketImpl.OPEN) {
                if (callbackId) {
                    this.callbacks.delete(callbackId);
                }
                callback?.({ message: "socket_not_open" });
                return false;
            }

            this._socket.send(
                JSON.stringify({
                    event: eventName,
                    args,
                    callback_id: callbackId,
                }),
            );
            return true;
        }

        return super.emit(eventName, ...args);
    }

    close() {
        this.disableReconnect();
        if (!this._socket) {
            this.failPendingCallbacks({ message: "socket_closed" });
            return;
        }

        this._socket.close();
        this._socket = null;
        this.connected = false;
        this.failPendingCallbacks({ message: "socket_closed" });
    }

    disconnect() {
        this.close();
    }

    forceDisconnectWithoutEvent() {}

    disableReconnect() {
        this.shouldReconnect = false;
        if (this.reconnectTimer) {
            window.clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }

    sendMessage(body) {
        if (!this._socket || this._socket.readyState !== this.WebSocketImpl.OPEN) {
            return false;
        }

        this._socket.send(JSON.stringify(body));
        return true;
    }
}

export default ShareJsSocket;
